/**
 * Orders API
 *
 * API methods for order-related operations.
 */

import { api } from './client';
import type {
  OrderListQuery,
  OrderListResponse,
  OrderDetailResponse,
  TransitionRequest,
  TransitionResponse,
  FileUploadRequest,
  FileUploadResponse,
  ApprovalRequest,
  ApprovalResponse,
  ApprovalPreviewResponse,
} from '../types';

/**
 * Get paginated list of orders with optional filtering
 */
export async function getOrders(query?: OrderListQuery): Promise<OrderListResponse> {
  return api.get<OrderListResponse>('/orders', {
    stage: query?.stage,
    excludeStage: query?.excludeStage,
    search: query?.search,
    page: query?.page,
    limit: query?.limit,
    startDate: query?.startDate,
    endDate: query?.endDate,
    filter: query?.filter,
    sortBy: query?.sortBy,
    sortDirection: query?.sortDirection,
  });
}

/**
 * Get full order details including files and approvals
 */
export async function getOrder(orderId: string): Promise<OrderDetailResponse> {
  return api.get<OrderDetailResponse>(`/orders/${orderId}`);
}

/**
 * Transition an order to a new stage
 */
export async function transitionOrder(
  orderId: string,
  request: TransitionRequest
): Promise<TransitionResponse> {
  return api.post<TransitionResponse>(`/orders/${orderId}/transition`, request);
}

/**
 * Get a signed URL for file upload
 * Backend route: POST /api/files/upload
 */
export async function getFileUploadUrl(
  orderId: string,
  request: FileUploadRequest
): Promise<FileUploadResponse> {
  return api.post<FileUploadResponse>('/files/upload', {
    orderId,
    ...request,
  });
}

/**
 * Confirm a file upload is complete
 * Backend route: POST /api/files/:fileId/confirm
 */
export async function confirmFileUpload(
  _orderId: string,
  fileId: string,
  sizeBytes: number
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/files/${fileId}/confirm`, { sizeBytes });
}

/**
 * Delete a file from an order
 * Backend route: DELETE /api/files/:fileId
 */
export async function deleteFile(
  _orderId: string,
  fileId: string
): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/files/${fileId}`);
}

/**
 * Get a download URL for a file
 * Backend route: GET /api/files/:fileId
 */
export async function getFileDownloadUrl(
  _orderId: string,
  fileId: string
): Promise<{ downloadUrl: string }> {
  const file = await api.get<{ downloadUrl: string }>(`/files/${fileId}`);
  return { downloadUrl: file.downloadUrl };
}

/**
 * Preview an approval email without sending
 * Backend route: POST /api/approvals/preview
 */
export async function previewApproval(
  orderId: string,
  request: ApprovalRequest
): Promise<ApprovalPreviewResponse> {
  return api.post<ApprovalPreviewResponse>('/approvals/preview', {
    orderId,
    ...request,
  });
}

/**
 * Send an approval request email
 * Backend route: POST /api/approvals
 */
export async function sendApproval(
  orderId: string,
  request: ApprovalRequest
): Promise<ApprovalResponse> {
  return api.post<ApprovalResponse>('/approvals', {
    orderId,
    ...request,
  });
}

/**
 * Resend an approval email
 * Backend route: POST /api/approvals/:approvalId/resend
 */
export async function resendApproval(
  _orderId: string,
  approvalId: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/approvals/${approvalId}/resend`);
}

/**
 * Cancel a pending approval
 * Backend route: POST /api/approvals/:approvalId/cancel
 */
export async function cancelApproval(
  _orderId: string,
  approvalId: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/approvals/${approvalId}/cancel`);
}

/**
 * Delete a tracked order from the production pipeline
 * Backend route: DELETE /api/orders/:orderId
 */
export async function deleteOrder(orderId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/orders/${orderId}`);
}

/**
 * Manually approve an order (staff action)
 * Backend route: POST /api/orders/:orderId/manual-approve
 */
export async function manualApprove(
  orderId: string,
  notes?: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/orders/${orderId}/manual-approve`, { notes });
}

// ============================================
// Shopify Order Search & Registration
// ============================================

/**
 * Shopify order from search results
 */
export interface ShopifyOrderSearchResult {
  id: string;
  name: string;
  createdAt: string;
  customer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  lineItems: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        quantity: number;
        sku?: string;
        product?: {
          id: string;
          title: string;
        };
      };
    }>;
  };
  totalPriceSet: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  fulfillmentStatus: string;
  displayFinancialStatus: string;
  isTracked: boolean;
}

/**
 * Response from Shopify order search
 */
export interface ShopifyOrderSearchResponse {
  orders: ShopifyOrderSearchResult[];
  hasNextPage: boolean;
  endCursor?: string;
}

/**
 * Search Shopify orders
 * Backend route: GET /api/orders/search/shopify
 */
export async function searchShopifyOrders(
  query: string,
  limit: number = 10,
  cursor?: string
): Promise<ShopifyOrderSearchResponse> {
  return api.get<ShopifyOrderSearchResponse>('/orders/search/shopify', {
    query,
    limit,
    cursor,
  });
}

/**
 * Register a Shopify order as a tracked order
 * Backend route: POST /api/orders/register
 */
export async function registerOrder(
  shopifyOrderId: string,
  notes?: string
): Promise<{ success: boolean; order: import('../types').TrackedOrder }> {
  return api.post('/orders/register', { shopifyOrderId, notes });
}

// ============================================
// Shopify Draft Order Search & Registration
// ============================================

/**
 * Shopify draft order from search results
 */
export interface ShopifyDraftOrderSearchResult {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  customer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  lineItems: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        quantity: number;
        sku?: string;
        product?: {
          id: string;
          title: string;
        };
      };
    }>;
  };
  totalPriceSet: {
    shopMoney: {
      amount: string;
      currencyCode: string;
    };
  };
  order?: { id: string } | null;
  isTracked: boolean;
}

/**
 * Response from Shopify draft order search
 */
export interface ShopifyDraftOrderSearchResponse {
  draftOrders: ShopifyDraftOrderSearchResult[];
  hasNextPage: boolean;
  endCursor?: string;
}

/**
 * Search Shopify draft orders
 * Backend route: GET /api/orders/search/shopify-drafts
 */
export async function searchShopifyDraftOrders(
  query: string,
  limit: number = 10,
  cursor?: string
): Promise<ShopifyDraftOrderSearchResponse> {
  return api.get<ShopifyDraftOrderSearchResponse>('/orders/search/shopify-drafts', {
    query,
    limit,
    cursor,
  });
}

/**
 * Register a Shopify draft order as a tracked order
 * Backend route: POST /api/orders/register-draft
 */
export async function registerDraftOrder(
  shopifyDraftOrderId: string,
  notes?: string
): Promise<{ success: boolean; order: import('../types').TrackedOrder }> {
  return api.post('/orders/register-draft', { shopifyDraftOrderId, notes });
}

/**
 * Add a line item to a draft order
 * Backend route: POST /api/orders/:orderId/add-line-item
 */
export async function addLineItemToDraft(
  orderId: string,
  item: { variantId: string; quantity: number }
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/orders/${orderId}/add-line-item`, item);
}

/**
 * Set the product type for an order
 * Backend route: PUT /api/orders/:orderId/product-type
 */
export async function setOrderProductType(
  orderId: string,
  productType: 'embroidery' | 'patch' | 'both' | 'undetermined'
): Promise<{ success: boolean }> {
  return api.put<{ success: boolean }>(`/orders/${orderId}/product-type`, { productType });
}

/**
 * Send a composed email from the Communication Center
 * Backend route: POST /api/email/compose
 */
export async function composeEmail(
  orderId: string,
  params: { to: string; subject: string; body: string }
): Promise<{ success: boolean; messageId?: string }> {
  return api.post<{ success: boolean; messageId?: string }>('/email/compose', {
    orderId,
    ...params,
  });
}

/**
 * Send an email from a connected Gmail account
 * Backend route: POST /api/email/gmail-compose
 */
export async function composeGmailEmail(
  orderId: string,
  params: { accountId: string; to: string; subject: string; body: string }
): Promise<{ success: boolean; messageId?: string }> {
  return api.post<{ success: boolean; messageId?: string }>('/email/gmail-compose', {
    orderId,
    ...params,
  });
}

/**
 * Re-order: create a new draft order from an existing tracked order
 * Backend route: POST /api/orders/:orderId/reorder
 */
export async function reorderOrder(
  orderId: string,
  params: {
    lineItems: Array<{ variantId: string; quantity: number }>;
    skipApproval: boolean;
  }
): Promise<{ success: boolean; newOrderId: string; newOrderName: string }> {
  return api.post(`/orders/${orderId}/reorder`, params);
}

/**
 * Set the size selection product IDs for a draft order
 * Backend route: PUT /api/orders/:orderId/size-selection-products
 */
export async function setSizeSelectionProducts(
  orderId: string,
  productIds: string[]
): Promise<{ success: boolean; productIds: string[] }> {
  return api.put<{ success: boolean; productIds: string[] }>(
    `/orders/${orderId}/size-selection-products`,
    { productIds }
  );
}
